import React, { useState, useEffect, useCallback } from "react";
import { expensesApi } from "../../../services/expensesApi";
import { adminApi } from "../../../services";
import { Icons } from "../../shared/Icons";
import { Loading, ErrorState } from "../../shared";
import { formatMonthYearLong, getTodayMonthYearBrazil } from "../../../utils/dateUtils";
import { formatBalanceCurrency } from "../AdminDashboard/balanceShared";
import { useCompany } from "../../../contexts/CompanyContext";

interface MonthlyProfitProps {
  month?: number;
  year?: number;
  /** Called when the month/year changes so parent can sync */
  onMonthYearChange?: (month: number, year: number) => void;
}

export const MonthlyProfit: React.FC<MonthlyProfitProps> = ({
  month: propMonth,
  year: propYear,
  onMonthYearChange,
}) => {
  const { company } = useCompany();
  const [monthYear, setMonthYear] = useState(() => {
    const today = getTodayMonthYearBrazil();
    return { month: propMonth ?? today.month, year: propYear ?? today.year };
  });
  const { month, year } = monthYear;

  const [revenue, setRevenue] = useState<number | null>(null);
  const [expenses, setExpenses] = useState<number | null>(null);
  const [byCategory, setByCategory] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (propMonth != null && propYear != null) {
      setMonthYear({ month: propMonth, year: propYear });
    }
  }, [propMonth, propYear]);

  const goPrevMonth = () => {
    setMonthYear(({ month: m, year: y }) => {
      const next = m <= 1 ? { month: 12, year: y - 1 } : { month: m - 1, year: y };
      onMonthYearChange?.(next.month, next.year);
      return next;
    });
  };
  const goNextMonth = () => {
    setMonthYear(({ month: m, year: y }) => {
      const next = m >= 12 ? { month: 1, year: y + 1 } : { month: m + 1, year: y };
      onMonthYearChange?.(next.month, next.year);
      return next;
    });
  };

  const fetchData = useCallback(async () => {
    if (!company) return;
    setLoading(true);
    setError(null);
    try {
      const [revenueRes, expenseRes] = await Promise.all([
        adminApi.getMonthlyBalance(month, year),
        expensesApi.monthSummary(month, year),
      ]);

      if (revenueRes.success && revenueRes.data) {
        setRevenue(revenueRes.data.total ?? 0);
      } else {
        setRevenue(0);
      }

      if (expenseRes.success && expenseRes.data) {
        setExpenses(expenseRes.data.total);
        setByCategory(expenseRes.data.byCategory);
      } else {
        setExpenses(0);
        setByCategory({});
      }
    } catch {
      setError("Erro ao carregar dados de lucratividade");
    } finally {
      setLoading(false);
    }
  }, [company, month, year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const profit = revenue !== null && expenses !== null ? revenue - expenses : null;
  const profitMargin =
    revenue !== null && revenue > 0 && profit !== null ? (profit / revenue) * 100 : null;

  return (
    <div className="bg-white dark:!bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 dark:text-slate-100 mb-3 sm:mb-4">
          Lucro Real do Mês
        </h3>

        <div className="flex items-center justify-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={goPrevMonth}
            className="flex-shrink-0 p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Icons.ChevronLeft sx={{ fontSize: 24 }} />
          </button>
          <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white text-center">
            {formatMonthYearLong(month, year)}
          </span>
          <button
            type="button"
            onClick={goNextMonth}
            className="flex-shrink-0 p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Icons.ChevronRight sx={{ fontSize: 24 }} />
          </button>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 rounded-lg p-3 sm:p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Receita</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    {revenue !== null ? formatBalanceCurrency(revenue) : "—"}
                  </p>
                </div>
                <Icons.TrendingUp sx={{ fontSize: 28 }} className="text-blue-500" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950 dark:to-rose-950 rounded-lg p-3 sm:p-4 border border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Despesas</p>
                  <p className="text-lg sm:text-xl font-bold text-red-700 dark:text-red-400">
                    {expenses !== null ? formatBalanceCurrency(expenses) : "—"}
                  </p>
                </div>
                <Icons.TrendingDown sx={{ fontSize: 28 }} className="text-rose-700 dark:text-rose-400" />
              </div>
            </div>

            <div
              className={`rounded-lg p-3 sm:p-4 border ${
                profit !== null && profit >= 0
                  ? "bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950 dark:to-green-950 border-emerald-200 dark:border-emerald-800"
                  : "bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950 dark:to-red-950 border-orange-200 dark:border-orange-800"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 dark:text-slate-400 mb-1">Lucro Líquido</p>
                  <p
                    className={`text-lg sm:text-xl font-bold ${
                      profit !== null && profit >= 0
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-orange-700 dark:text-orange-400"
                    }`}
                  >
                    {profit !== null ? formatBalanceCurrency(profit) : "—"}
                  </p>
                  {profitMargin !== null && (
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                      {profitMargin.toFixed(1)}% de margem
                    </p>
                  )}
                </div>
                <Icons.Money
                  sx={{
                    fontSize: 28,
                    color: profit !== null && profit >= 0 ? "#059669" : "#ea580c",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Expenses breakdown bar */}
          {Object.keys(byCategory).length > 0 && (
            <div>
              <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">
                Despesas por Categoria
              </p>
              <div className="space-y-2">
                {Object.entries(byCategory)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, total]) => (
                    <div key={cat} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 dark:text-slate-400 w-24 shrink-0 truncate">{cat}</span>
                      <div className="flex-1 bg-gray-100 dark:bg-slate-700 rounded-full h-2">
                        <div
                          className="bg-red-400 h-2 rounded-full"
                          style={{
                            width: `${expenses && expenses > 0 ? (total / expenses) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-gray-700 dark:text-slate-200 w-24 text-right shrink-0">
                        {formatBalanceCurrency(total)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Zero state */}
          {revenue === 0 && expenses === 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-200 text-center">
                Nenhum dado de receita ou despesa registrado para este mês
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
